import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from "@/components/ui/card";
import { cn, getImageUrl } from '@/lib/utils';
import { Property } from '@/types/property';
import { ChevronLeft, ChevronRight, ImageIcon, X } from 'lucide-react';
import { useEffect } from 'react';

interface ImageGalleryProps {
  property: Property;
  galleryOpen: boolean;
  setGalleryOpen: (open: boolean) => void;
  activeGalleryImage: number;
  setActiveGalleryImage: React.Dispatch<React.SetStateAction<number>>;
}

// Modal component that renders on top of everything else
export function GalleryModal({
  property,
  galleryOpen,
  setGalleryOpen,
  activeGalleryImage,
  setActiveGalleryImage
}: ImageGalleryProps) {
  const hasImages = property.images && property.images.length > 0;
  const totalImages = hasImages ? property.images.length : 0;

  // Handle keyboard navigation in gallery
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!galleryOpen || !hasImages) return;

      if (e.key === 'ArrowRight') {
        setActiveGalleryImage((prev) => (prev + 1) % totalImages);
      } else if (e.key === 'ArrowLeft') {
        setActiveGalleryImage((prev) => prev === 0 ? totalImages - 1 : prev - 1);
      } else if (e.key === 'Escape') {
        setGalleryOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, totalImages, setActiveGalleryImage, setGalleryOpen, hasImages]);

  if (!galleryOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header with navigation and close button */}
      <div className="p-4 flex items-center justify-between text-white">
        <button
          onClick={() => setGalleryOpen(false)}
          className="flex items-center text-white hover:text-gray-300 transition"
        >
          <X className="h-5 w-5 mr-2" />
          Back to listing
        </button>
        {hasImages && (
          <div className="text-white font-medium">
            {activeGalleryImage + 1} of {totalImages}
          </div>
        )}
      </div>

      {/* Main image display */}
      <div className="flex-1 flex items-center justify-center relative">
        {hasImages ? (
          <img
            src={getImageUrl(property.images[activeGalleryImage].original_url, {
              width: 1600,
              height: 1200,
              resize: 'contain',
              quality: 85
            })}
            alt={`Property image ${activeGalleryImage + 1}`}
            className="max-h-[calc(100vh-120px)] max-w-full object-contain"
          />
        ) : (
          <div className="bg-gray-100 rounded-lg flex items-center justify-center w-[800px] h-[600px]">
            <ImageIcon className="h-24 w-24 text-gray-400" />
            <p className="text-gray-500 mt-4">No images available</p>
          </div>
        )}

        {/* Navigation arrows - only shown if there are images */}
        {hasImages && totalImages > 1 && (
          <>
            <button
              className="absolute left-4 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition"
              onClick={() => setActiveGalleryImage((prev) =>
                prev === 0 ? totalImages - 1 : prev - 1
              )}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <button
              className="absolute right-4 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition"
              onClick={() => setActiveGalleryImage((prev) =>
                (prev + 1) % totalImages
              )}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>

      {/* Property info at bottom */}
      <div className="bg-black/80 p-4 text-white">
        <div className="text-center">
          For Sale: C${property.list_price?.toLocaleString() || "519,900"} ({property.total_bedrooms || 3} beds, {property.total_bathrooms || 1} bath, {property.approx_square_footage?.toLocaleString() || "1,182"} Square Feet)
        </div>
      </div>
    </div >
  );
}

// Preview component shown inline in the page
export default function ImageGallery({
  property,
  galleryOpen,
  setGalleryOpen,
  activeGalleryImage,
  setActiveGalleryImage
}: ImageGalleryProps) {
  const hasImages = property.images && property.images.length > 0;
  const totalImages = hasImages ? property.images.length : 0;

  const openGallery = (index: number) => {
    if (hasImages) {
      setActiveGalleryImage(index);
      setGalleryOpen(true);
    }
  };

  return (
    <>
      {/* Gallery Modal rendered at the portal level */}
      <GalleryModal
        property={property}
        galleryOpen={galleryOpen}
        setGalleryOpen={setGalleryOpen}
        activeGalleryImage={activeGalleryImage}
        setActiveGalleryImage={setActiveGalleryImage}
      />

      {/* Image Gallery Preview */}
      <Card className="relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 h-[500px]">
          {/* Main large image */}
          <div
            className={cn(
              "md:col-span-2 relative overflow-hidden rounded-lg h-full",
              hasImages && "cursor-pointer"
            )}
            onClick={() => hasImages && openGallery(0)}
          >
            {hasImages ? (
              <img
                src={getImageUrl(property.images[0].original_url, {
                  width: 800,
                  height: 600,
                  resize: 'cover',
                  quality: 80
                })}
                alt="Property main image"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                <ImageIcon className="h-24 w-24 text-gray-400" />
                <p className="text-gray-500 mt-4">No images available</p>
              </div>
            )}
            <div className="absolute top-3 left-3">
              <Badge
                className={cn(
                  "hover:bg-opacity-90",
                  property.status === 'archive' ? "bg-gray-600 hover:bg-gray-700" : "bg-red-600 hover:bg-red-700"
                )}
              >
                {property.status === 'archive' ? "Sold" : "For Sale"}
              </Badge>
            </div>
          </div>

          {/* 2x2 grid of smaller images */}
          <div className="hidden md:grid md:col-span-2 grid-cols-2 grid-rows-2 gap-2 h-full">
            {[1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className={cn(
                  "relative overflow-hidden rounded-lg",
                  hasImages && index < totalImages && "cursor-pointer"
                )}
                onClick={() => hasImages && index < totalImages && openGallery(index)}
              >
                {hasImages && index < totalImages ? (
                  <img
                    src={getImageUrl(property.images[index].original_url, {
                      width: 400,
                      height: 300,
                      resize: 'cover',
                      quality: 70
                    })}
                    alt={`Property thumbnail ${index}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                {hasImages && index === 4 && totalImages > 5 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white font-medium">+{totalImages - 5} photos</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile gallery preview (single row of images) */}
          <div className="md:hidden flex gap-2 overflow-x-auto mt-2">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className={cn(
                  "w-24 h-16 overflow-hidden rounded-lg",
                  hasImages && index < totalImages && "cursor-pointer"
                )}
                onClick={() => hasImages && index < totalImages && openGallery(index)}
              >
                {hasImages && index < totalImages ? (
                  <img
                    src={getImageUrl(property.images[index].original_url, {
                      width: 200,
                      height: 150,
                      resize: 'cover',
                      quality: 70
                    })}
                    alt={`Property thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <ImageIcon className="h-6 w-6 text-gray-400" />
                  </div>
                )}
              </div>
            ))}
            {hasImages && totalImages > 3 && (
              <div
                className="w-24 h-16 relative rounded-lg cursor-pointer overflow-hidden"
                onClick={() => openGallery(3)}
              >
                <img
                  src={getImageUrl(property.images[3].original_url, {
                    width: 200,
                    height: 150,
                    resize: 'cover',
                    quality: 70
                  })}
                  alt="More photos"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">+{totalImages - 3}</span>
                </div>
              </div>
            )}
          </div>

          {/* "See all photos" button - only shown if there are images */}
          {hasImages && (
            <Button
              size="sm"
              variant="secondary"
              className="absolute bottom-3 right-3 z-10"
              onClick={() => openGallery(0)}
            >
              <ImageIcon className="h-4 w-4 mr-1" />
              See all {totalImages} photos
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}